/**
 * 权限控制 Hook
 * 提供权限信息加载、权限判断和角色判断功能
 * 集成 PermissionCache 实现缓存优先策略
 *
 * 自动刷新策略：
 * - 首次加载：缓存优先（命中即用，无 loading 抖动）
 * - 窗口重新获焦 / tab 切回：若缓存已过新鲜期（5 分钟），后台静默刷新
 * - 多组件共享去重：通过模块级 lastFetchPromise 保证并发安全
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PermissionCache } from '../utils/permissionCache';
import * as authService from '../services/api/auth';
import type { PermissionInfo } from '../types/models';

export interface UsePermissionReturn {
  permissionInfo: PermissionInfo | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  refreshPermission: () => Promise<void>;
}

// 模块级：跨组件共享的去重锁，避免多个 usePermission 同时触发请求
let inflightFetch: Promise<PermissionInfo> | null = null;

function fetchPermissionInfoOnce(): Promise<PermissionInfo> {
  if (inflightFetch) return inflightFetch;
  inflightFetch = authService.getPermissionInfo().finally(() => {
    inflightFetch = null;
  });
  return inflightFetch;
}

export function usePermission(): UsePermissionReturn {
  const [permissionInfo, setPermissionInfo] = useState<PermissionInfo | null>(() => PermissionCache.get());
  const [loading, setLoading] = useState<boolean>(() => !PermissionCache.get());
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadPermissionInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 优先从缓存获取（无抖动地显示数据）
      const cached = PermissionCache.get();
      if (cached) {
        setPermissionInfo(cached);
        setLoading(false);
        return;
      }

      // 缓存未命中，从服务端获取
      const info = await fetchPermissionInfoOnce();
      PermissionCache.save(info);
      if (mountedRef.current) setPermissionInfo(info);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '加载权限信息失败';
      if (mountedRef.current) {
        setError(errorMessage);
        setPermissionInfo(null);
      }
      console.error('加载权限信息失败:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const refreshPermission = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await fetchPermissionInfoOnce();
      PermissionCache.save(info);
      if (mountedRef.current) setPermissionInfo(info);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '刷新权限信息失败';
      if (mountedRef.current) setError(errorMessage);
      console.error('刷新权限信息失败:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // 后台静默刷新：不触发 loading 抖动，仅更新数据
  const silentRefresh = useCallback(async () => {
    try {
      const info = await fetchPermissionInfoOnce();
      PermissionCache.save(info);
      if (mountedRef.current) setPermissionInfo(info);
    } catch (err) {
      console.warn('后台刷新权限失败:', err);
    }
  }, []);

  // 监听窗口获焦：若缓存已过新鲜期，后台静默刷新
  useEffect(() => {
    const onFocus = () => {
      if (PermissionCache.isStale()) silentRefresh();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && PermissionCache.isStale()) {
        silentRefresh();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [silentRefresh]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!permissionInfo || !permission) return false;
    if (permissionInfo.user?.is_superuser) return true;
    return permissionInfo.permissions.includes(permission);
  }, [permissionInfo]);

  const hasRole = useCallback((role: string): boolean => {
    if (!permissionInfo || !role) return false;
    return permissionInfo.roles.includes(role);
  }, [permissionInfo]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!permissionInfo || !permissions || permissions.length === 0) return false;
    if (permissionInfo.user?.is_superuser) return true;
    return permissions.some((p) => permissionInfo.permissions.includes(p));
  }, [permissionInfo]);

  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    if (!permissionInfo || !permissions || permissions.length === 0) return false;
    if (permissionInfo.user?.is_superuser) return true;
    return permissions.every((p) => permissionInfo.permissions.includes(p));
  }, [permissionInfo]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    if (!permissionInfo || !roles || roles.length === 0) return false;
    return roles.some((r) => permissionInfo.roles.includes(r));
  }, [permissionInfo]);

  const hasAllRoles = useCallback((roles: string[]): boolean => {
    if (!permissionInfo || !roles || roles.length === 0) return false;
    return roles.every((r) => permissionInfo.roles.includes(r));
  }, [permissionInfo]);

  useEffect(() => {
    loadPermissionInfo();
  }, [loadPermissionInfo]);

  return {
    permissionInfo,
    loading,
    error,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyRole,
    hasAllRoles,
    refreshPermission,
  };
}

